import React from 'react';
import { Icon } from 'antd';
import shepherd from 'shepherd.js';
import { StepOptionsButton } from 'shepherd.js/dist/cjs/step';
import { renderToString } from 'react-dom/server';
import cx from 'classnames';

export const SYSTEM_TOUR_SHOWED_KEY = 'system_tour_showed';
export const AUTH_TOUR_SHOWED_KEY = 'auth_tour_showed';
export const NO_PROMOTION_CODE = 'no_promotion_code';

const CLASS_NAME = 'laigu-shepherd-theme';

export const tourInstance = new shepherd.Tour({
  useModalOverlay: true,
  defaultStepOptions: {
    classes: CLASS_NAME,
    cancelIcon: { enabled: true }
  }
});

const prevButton: StepOptionsButton = {
  text: renderToString(<Icon type='left' />),
  classes: 'shepherd-button-prev',
  action: () => tourInstance.trigger(TOUR_EVENTS.prev)
};
const nextButton: StepOptionsButton = {
  text: renderToString(<Icon type='right' />),
  classes: 'shepherd-button-next',
  action: () => tourInstance.trigger(TOUR_EVENTS.next)
};
const completeButton: StepOptionsButton = {
  text: '完成',
  classes: 'shepherd-button-complete',
  action: () => tourInstance.trigger(TOUR_EVENTS.cancel)
};

export const MODAL_IMG_CLS = 'shepherd-modal-place';
export const QUICK_JUMPER_CLS = 'shepherd-quick-jumper';
const getContent = (text: string, count: number, activeIndex: number) => {
  return renderToString(
    <>
      {text}
      <div className={QUICK_JUMPER_CLS}>
        {Array(count)
          .fill(0)
          .map((_, index) => (
            <div
              key={index}
              data-index={index}
              className={cx(`${QUICK_JUMPER_CLS}-item`, { [`${QUICK_JUMPER_CLS}-item-active`]: activeIndex === index })}
            />
          ))}
      </div>
    </>
  );
};

export const TOUR_EVENTS = {
  next: 'next',
  prev: 'prev',
  jump: 'jump',
  cancel: 'cancel'
};

export enum Tours {
  AUTH = 'auth',
  CHAT = 'chat',
  HISTORY = 'history',
  AGENT = 'agent',
  REPORT = 'report'
}

export const TOUR_LIST = [
  { key: Tours.CHAT, title: '查看对话消息', url: '/chat' },
  { key: Tours.HISTORY, title: '查看历史对话', url: '/history' },
  { key: Tours.AGENT, title: '管理客服账号', url: '/setting/team/agent' },
  { key: Tours.REPORT, title: '查看数据', url: '/report/overview' }
];

const getStepsCls = (key: Tours, step: number) =>
  Array(step)
    .fill(0)
    .map((_, index) => `laigu-tour-guide-${key}-${index}`);

const AUTH_STEP_COUNT = 2;
const CHAT_STEP_COUNT = 3;
const HISTORY_STEP_COUNT = 4;
const AGENT_STEP_COUNT = 3;
const REPORT_STEP_COUNT = 1;

export const AUTH_STEPS_CLS = getStepsCls(Tours.AUTH, CHAT_STEP_COUNT);
export const CHAT_STEPS_CLS = getStepsCls(Tours.CHAT, CHAT_STEP_COUNT);
export const HISTORY_STEPS_CLS = getStepsCls(Tours.HISTORY, HISTORY_STEP_COUNT);
export const AGENT_STEPS_CLS = getStepsCls(Tours.AGENT, AGENT_STEP_COUNT);
export const REPORT_STEPS_CLS = getStepsCls(Tours.REPORT, REPORT_STEP_COUNT);

const AUTH_STEPS = [
  new shepherd.Step(tourInstance, {
    title: '授权小红书账号',
    text: getContent('点击「授权接入」前往小红书后台授权绑定账号', AUTH_STEP_COUNT, 0),
    attachTo: {
      element: `.${AUTH_STEPS_CLS[0]}`,
      on: 'bottom-end'
    },
    classes: `${CLASS_NAME}-bottom`,
    buttons: [nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '授权成功，该列表显示已授权接入的全部账号',
    text: getContent('绑定状态显示为「正常」后，可获取小红书私信消息', AUTH_STEP_COUNT, 1),
    attachTo: {
      element: `.${MODAL_IMG_CLS}`,
      on: 'bottom'
    },
    classes: `${CLASS_NAME}-modal`,
    buttons: [prevButton, completeButton],
    beforeShowPromise: () =>
      new Promise(resolve => {
        setTimeout(() => {
          window.scrollTo(0, 0);
          resolve(null);
        }, 100);
      })
  })
];
const CHAT_STEPS = [
  new shepherd.Step(tourInstance, {
    title: '对话工作台',
    text: getContent('选中「对话」，进入在线客服对话页面', CHAT_STEP_COUNT, 0),
    attachTo: {
      element: `.${CHAT_STEPS_CLS[0]}`,
      on: 'right-start'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '查看对话列表',
    text: getContent('切换导航，可查看「我的、同事、@我」对话列表', CHAT_STEP_COUNT, 1),
    attachTo: {
      element: `.${CHAT_STEPS_CLS[1]}`,
      on: 'right'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [prevButton, nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '切换坐席在线状态',
    text: getContent('切换坐席状态为「在线」状态，对话将可分配', CHAT_STEP_COUNT, 2),
    attachTo: {
      element: `.${CHAT_STEPS_CLS[2]}`,
      on: 'bottom-start'
    },
    classes: `${CLASS_NAME}-bottom`,
    buttons: [prevButton, completeButton],
    beforeShowPromise: () => new Promise<void>(resolve => setTimeout(() => resolve(), 50))
  })
];
const HISTORY_STEPS = [
  new shepherd.Step(tourInstance, {
    title: '历史对话',
    text: getContent('选中「历史对话」，可查看全部对话记录', HISTORY_STEP_COUNT, 0),
    attachTo: {
      element: `.${HISTORY_STEPS_CLS[0]}`,
      on: 'right-start'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '定义筛选条件',
    text: getContent('设置筛选条件，搜索历史对话', HISTORY_STEP_COUNT, 1),
    attachTo: {
      element: `.${HISTORY_STEPS_CLS[1]}`,
      on: 'right-end'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [prevButton, nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '历史对话列表',
    text: getContent('搜索完成，显示符合条件的历史对话', HISTORY_STEP_COUNT, 2),
    attachTo: {
      element: `.${MODAL_IMG_CLS}`,
      on: 'bottom'
    },
    classes: `${CLASS_NAME}-modal`,
    buttons: [prevButton, nextButton],
    beforeShowPromise: () => new Promise<void>(resolve => setTimeout(() => resolve(), 100))
  }),
  new shepherd.Step(tourInstance, {
    title: '查看历史对话详情',
    text: getContent('点击任意「历史对话」，抽屉展开显示该条历史对话详情', HISTORY_STEP_COUNT, 3),
    attachTo: {
      element: `.${MODAL_IMG_CLS}`,
      on: 'bottom'
    },
    classes: `${CLASS_NAME}-modal`,
    buttons: [prevButton, completeButton],
    beforeShowPromise: () => new Promise<void>(resolve => setTimeout(() => resolve(), 100))
  })
];
const AGENT_STEPS = [
  new shepherd.Step(tourInstance, {
    title: '切换选择设置项',
    text: getContent('选择「公共设置」', AGENT_STEP_COUNT, 0),
    attachTo: {
      element: `.${AGENT_STEPS_CLS[0]}`,
      on: 'right-end'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '客服与分组',
    text: getContent('选中「公共设置-企业管理-客服与分组」', AGENT_STEP_COUNT, 1),
    attachTo: {
      element: `.${AGENT_STEPS_CLS[1]}`,
      on: 'right-start'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [prevButton, nextButton]
  }),
  new shepherd.Step(tourInstance, {
    title: '添加客服',
    text: getContent('通过「添加客服账号」，可添加团队成员', AGENT_STEP_COUNT, 2),
    attachTo: {
      element: `.${AGENT_STEPS_CLS[2]}`,
      on: 'bottom-start'
    },
    classes: `${CLASS_NAME}-bottom`,
    buttons: [prevButton, completeButton]
  })
];
const REPORT_STEPS = [
  new shepherd.Step(tourInstance, {
    title: '报表',
    text: '可查看全部报表数据',
    attachTo: {
      element: `.${REPORT_STEPS_CLS[0]}`,
      on: 'right-end'
    },
    classes: `${CLASS_NAME}-right`,
    buttons: [completeButton]
  })
];
export const TOUR_STEPS: Record<Tours, any[]> = {
  [Tours.AUTH]: AUTH_STEPS,
  [Tours.CHAT]: CHAT_STEPS,
  [Tours.HISTORY]: HISTORY_STEPS,
  [Tours.AGENT]: AGENT_STEPS,
  [Tours.REPORT]: REPORT_STEPS
};
